import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { toast } from 'components/Toast';
import { logout, setAuth } from 'features/auth/userSlice';
import FetchError from 'utils/errors';

const noAuthEndpoints: string[] = ['login'];

function requestPath(args: string | FetchArgs): string {
  if (typeof args === 'string') return args;
  const u = (args as FetchArgs)?.url;
  return typeof u === 'string' ? u : '';
}

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
  args,
  api,
  extraOptions
) => {
  const state = api.getState();
  const baseUrl = state.user.apiUrl;
  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    credentials: 'include',
    prepareHeaders: (headers, { endpoint }) => {
      const { access } = state.user.tokens;
      if (access && !noAuthEndpoints.includes(endpoint)) {
        headers.set('Authorization', `Bearer ${access}`);
      } else {
        headers.delete('Authorization');
      }
      return headers;
    },
  });
  return rawBaseQuery(args, api, extraOptions);
};

const execRefreshToken = async (args: string | FetchArgs, api: BaseQueryApi) => {
  const { apiUrl, tokens } = api.getState().user;
  const { access, refresh } = tokens;

  if (!access) {
    api.dispatch(logout({}));
    return false;
  }

  const refreshResultRaw = await fetch(`${apiUrl}/api/auth/refresh-token`, {
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${access}`,
    },
    method: 'POST',
    body: JSON.stringify({}),
  });

  if (refreshResultRaw.status !== 200) {
    api.dispatch(logout({}));
    return false;
  }

  const refreshResult = await refreshResultRaw.json();
  if (!refreshResult.success || !refreshResult.token) {
    api.dispatch(logout({}));
    return false;
  }

  api.dispatch(
    setAuth({
      tokens: { access: refreshResult.token, refresh: refresh ?? refreshResult.token },
    })
  );
  return true;
};

const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraOptions: any
) => {
  let result = await dynamicBaseQuery(args, api, extraOptions);
  const error = result?.error;
  if (error?.status === 401) {
    const isRefreshed = await execRefreshToken(args, api);
    if (isRefreshed) {
      // re submit request
      result = await dynamicBaseQuery(args, api, extraOptions);
      return result;
    }
  } else if (error) {
    // handle different errors here
    let errMessage = '';
    if (error.status === 429) {
      errMessage = 'Too many requests, please try again after sometime.';
      throw new FetchError(errMessage, error.status);
    } else if (typeof error.status === 'number' && error.status >= 500) {
      errMessage = 'Service is temporarily unavailable. Please try again.';
      toast({ message: errMessage, severity: 'error' });
      throw new FetchError(errMessage, error.status);
    } else if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') {
      const path = requestPath(args);
      const isLogin = path.includes('/api/auth/login');
      errMessage =
        error.status === 'TIMEOUT_ERROR'
          ? 'Request timed out. Please try again.'
          : 'Connection failed. Please try again in a moment.';
      if (!isLogin) {
        toast({ message: errMessage, severity: 'error' });
      }
      throw new FetchError(errMessage, error.status);
    } else if (error.status === 'PARSING_ERROR' || error.status === 'CUSTOM_ERROR') {
      errMessage = 'Something went wrong while parsing the response.';
      toast({ message: errMessage, severity: 'error' });
      throw new FetchError(errMessage, error.status);
    }
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ['shop', 'CashierSummary', 'CashierProfile', 'WalletRequests'],
  endpoints: () => ({}),
});
